import { useState, type DragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CODIFICACOES,
  ROTULO_CODIFICACAO,
  ROTULO_ETAPA_DA_IMPORTACAO,
  ROTULO_SEPARADOR,
  ROTULO_STATUS_ALUNO,
  SEPARADORES,
  STATUS_IMPORTAVEIS,
  type Codificacao,
  type Separador,
  type StatusImportavel,
} from '@arena/shared/schemas';
import { mensagemDoErro } from '../api/cliente.js';
import { importacaoApi } from '../api/importacao.js';
import { instanteCompleto } from '../alunos/formatos.js';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Feed, ItemDeFeed, Nota, Status } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { problemaDoArquivo, resumoDaAnalise, SITUACAO_DA_ETAPA } from './formatos.js';

/**
 * `#m-importar` — "Importar base atual · Migração da planilha em uso hoje".
 *
 * O botão diz "Analisar arquivo", e é isso que ele faz: nenhum aluno é
 * gravado aqui. A análise volta com o relatório, e a gravação só acontece
 * na tela de conferência, depois de a pessoa ver o que vai entrar.
 *
 * Separador e codificação começam em "automático": a detecção acerta a
 * planilha comum, e a escolha manual existe para a que ela erra. A
 * situação padrão é a de quem entra sem coluna de situação na planilha.
 *
 * As importações recentes aparecem embaixo: fechar a conferência sem
 * confirmar não pode fazer a análise se perder.
 *
 * Ver: Arena_backend/Docs/plano/09-importacao-csv.md
 */

type OpcaoDeSeparador = 'automatico' | Separador;
type OpcaoDeCodificacao = 'automatica' | Codificacao;

export function ModalDeImportacao({
  onFechar,
  onAbrir,
}: {
  onFechar: () => void;
  /** A conferência de uma importação — a recém-analisada ou uma recente. */
  onAbrir: (id: string) => void;
}) {
  const cliente = useQueryClient();
  const avisar = useToast();

  const [arquivo, setArquivo] = useState<File | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const [separador, setSeparador] = useState<OpcaoDeSeparador>('automatico');
  const [codificacao, setCodificacao] = useState<OpcaoDeCodificacao>('automatica');
  const [status, setStatus] = useState<StatusImportavel>('Ativo');
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  const recentes = useQuery({
    queryKey: ['importacoes', 'lista'],
    queryFn: () => importacaoApi.listar(),
  });

  const analisar = useMutation({
    mutationFn: (escolhido: File) =>
      importacaoApi.analisar(escolhido, { nome: escolhido.name, separador, codificacao, status }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['importacoes'] }),
  });

  function escolher(escolhido: File | undefined) {
    setErroGeral(null);
    if (!escolhido) return;
    setArquivo(escolhido);
    // O problema do arquivo aparece ao escolher, e não só ao clicar: a
    // planilha do Excel precisa voltar ao Excel antes de qualquer coisa.
    setErroGeral(problemaDoArquivo(escolhido));
  }

  function soltar(evento: DragEvent<HTMLLabelElement>) {
    evento.preventDefault();
    setArrastando(false);
    escolher(evento.dataTransfer.files[0]);
  }

  async function enviar() {
    if (!arquivo) return;
    const problema = problemaDoArquivo(arquivo);
    if (problema) {
      setErroGeral(problema);
      return;
    }
    setErroGeral(null);
    try {
      const analise = await analisar.mutateAsync(arquivo);
      avisar(resumoDaAnalise(analise));
      onAbrir(analise.id);
    } catch (erro) {
      setErroGeral(mensagemDoErro(erro));
    }
  }

  const abertas = (recentes.data?.itens ?? []).slice(0, 5);
  const fuso = recentes.data?.fuso ?? 'America/Sao_Paulo';

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Importar base atual"
      descricao="Migração da planilha em uso hoje"
      largura="estreita"
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void enviar()} disabled={!arquivo || analisar.isPending}>
            {analisar.isPending ? 'Analisando…' : 'Analisar arquivo'}
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        <label
          className={['empty', 'zona-de-arquivo', arrastando && 'arrastando']
            .filter(Boolean)
            .join(' ')}
          onDragOver={(e) => {
            e.preventDefault();
            setArrastando(true);
          }}
          onDragLeave={() => setArrastando(false)}
          onDrop={soltar}
        >
          <input
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            className="sr-only"
            onChange={(e) => escolher(e.target.files?.[0])}
          />
          <Icone nome="clip" tamanho={26} />
          <br />
          {arquivo ? (
            <>
              <b>{arquivo.name}</b>
              <br />
              <small>Clique para trocar o arquivo</small>
            </>
          ) : (
            'Arraste o arquivo CSV ou clique para escolher'
          )}
        </label>

        <Campo rotulo="Separador" ajuda="Na dúvida, deixe no automático.">
          <select
            value={separador}
            onChange={(e) => setSeparador(e.target.value as OpcaoDeSeparador)}
          >
            <option value="automatico">Automático</option>
            {SEPARADORES.map((s) => (
              <option key={s} value={s}>
                {ROTULO_SEPARADOR[s]}
              </option>
            ))}
          </select>
        </Campo>

        <Campo
          rotulo="Codificação"
          ajuda="O Excel em português salva em ISO-8859-1; a detecção converte sem perder acento."
        >
          <select
            value={codificacao}
            onChange={(e) => setCodificacao(e.target.value as OpcaoDeCodificacao)}
          >
            <option value="automatica">Automática</option>
            {CODIFICACOES.map((c) => (
              <option key={c} value={c}>
                {ROTULO_CODIFICACAO[c]}
              </option>
            ))}
          </select>
        </Campo>

        <Campo
          rotulo="Situação de quem entra"
          ajuda="Vale para as linhas sem coluna de situação na planilha."
        >
          <select value={status} onChange={(e) => setStatus(e.target.value as StatusImportavel)}>
            {STATUS_IMPORTAVEIS.map((s) => (
              <option key={s} value={s}>
                {ROTULO_STATUS_ALUNO[s]}
              </option>
            ))}
          </select>
        </Campo>

        <Nota>
          Na importação o sistema faz de-para das colunas, aponta duplicidade por telefone e devolve
          um relatório com as linhas que precisam de correção antes de gravar. Consentimentos não
          são importados: cada aluno entra com o aceite do termo pendente, até ele mesmo aceitar.
        </Nota>

        {erroGeral && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{erroGeral}</div>
          </div>
        )}

        {abertas.length > 0 && (
          <div>
            <div className="hint" style={{ margin: '4px 0 8px' }}>
              Importações recentes
            </div>
            <Feed>
              {abertas.map((i) => (
                <ItemDeFeed
                  key={i.id}
                  titulo={i.nomeArquivo}
                  subtitulo={`${instanteCompleto(i.criadaEm, fuso)} · ${i.contadores.total} linhas${i.autor ? ` · ${i.autor}` : ''}`}
                  direita={
                    <Status situacao={SITUACAO_DA_ETAPA[i.etapa]}>
                      {ROTULO_ETAPA_DA_IMPORTACAO[i.etapa]}
                    </Status>
                  }
                  onClick={() => onAbrir(i.id)}
                />
              ))}
            </Feed>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}
